import React, { useEffect, useCallback, useState, useRef } from 'react';
import { X } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap.ts';
import { isBovensteVenster } from '../../utils/bovensteVenster.ts';
import { useTranslation } from '../../i18n.ts';

/* ────────────────────────────────────────────────────── */
/*  Modal — Unified overlay wrapper                       */
/*                                                        */
/*  Standards:                                            */
/*  • Backdrop:  bg-pt-forest/80 (brand consistent)       */
/*  • Radius:    rounded-[32px]                           */
/*  • Z-index:   z-[200]                                  */
/*  • Animation: fade-in + zoom-in-95 / fade-out + zoom   */
/*  • Dark mode: --bg-widget + --text-main                */
/*  • ARIA:      role=dialog, aria-modal, aria-labelledby  */
/*  • Focus:     useFocusTrap + Escape to close           */
/* ────────────────────────────────────────────────────── */

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalProps {
  /** Control visibility */
  isOpen: boolean;
  /** Called when user dismisses (Escape, backdrop click, X button) */
  onClose: () => void;
  /** Unique id for aria-labelledby — must match a heading id inside children */
  titleId: string;
  /** Width preset */
  size?: ModalSize;
  /** Extra classes on the content box */
  className?: string;
  /** Disable backdrop click to close */
  persistent?: boolean;
  /** Optional id for aria-describedby — links to a description element inside children */
  descriptionId?: string;
  /** Content */
  children: React.ReactNode;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  full: 'max-w-[95vw]',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  titleId,
  size = 'md',
  className = '',
  persistent = false,
  descriptionId,
  children,
}) => {
  // Track mounted state to allow exit animation before unmount
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);

  // Open: mount immediately
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
      closingRef.current = false;
    } else if (mounted && !closingRef.current) {
      // Close: start exit animation
      closingRef.current = true;
      setClosing(true);
      const timer = setTimeout(() => {
        setMounted(false);
        setClosing(false);
        closingRef.current = false;
      }, 150); // matches animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape key handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // #1836 — alleen het bovenste venster (een venster erboven sluit eerst zelf).
        if (!isBovensteVenster(focusTrapRef.current)) return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!mounted || closing) return;
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [mounted, closing, handleKeyDown]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted]);

  const focusTrapRef = useFocusTrap(mounted && !closing);

  // Track of de mousedown op de backdrop zelf begon (en niet in een child).
  // Zonder deze check sluit de modal onterecht wanneer een gebruiker tekst
  // selecteert in een input-veld en daarbij per ongeluk buiten de modal
  // loslaat: de click-event vuurt dan op de backdrop, maar de gebruiker
  // bedoelde te selecteren, niet te sluiten.
  const mouseDownOnBackdropRef = useRef(false);

  if (!mounted) return null;

  const state = closing ? 'closing' : 'open';

  return (
    <div
      ref={focusTrapRef}
      className={`modal-backdrop fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-pt-forest/80`}
      data-state={state}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onMouseDown={(e) => {
        mouseDownOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (persistent) return;
        // Beide eisen: mousedown-origin én click-target moeten de backdrop
        // zelf zijn. Voorkomt sluiten bij text-selectie-drag vanuit content.
        if (!mouseDownOnBackdropRef.current) return;
        if (e.target !== e.currentTarget) return;
        onClose();
      }}
    >
      <div
        className={`
          modal-content w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden
          bg-pt-cream dark:bg-[var(--bg-widget)]
          rounded-[32px] shadow-2xl will-change-transform
          ${className}
        `}
        data-state={state}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────── */
/*  Sub-components for consistent structure               */
/* ────────────────────────────────────────────────────── */

interface ModalHeaderProps {
  /** Title text */
  title: string;
  /** Must match Modal's titleId */
  titleId: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Icon element (e.g. <Users size={24} />) */
  icon?: React.ReactNode;
  /** Close handler */
  onClose: () => void;
  /** Extra classes */
  className?: string;
  /** Optionele link-achtige actie aan rechterkant, links van het kruisje
   * (bijv. "Type wijzigen" in EventModal). */
  extraAction?: { label: string; onClick: () => void; title?: string };
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  titleId,
  subtitle,
  icon,
  onClose,
  className = '',
  extraAction,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`p-4 md:p-6 border-b border-[var(--border-subtle)] flex justify-between items-center ${className}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {icon && (
          <span className="text-pt-gold-text dark:text-[var(--gold-text)] shrink-0">{icon}</span>
        )}
        <div className="min-w-0">
          <h2
            id={titleId}
            className="text-xl font-bold text-pt-forest dark:text-[var(--text-main)] truncate"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-sm text-pt-sage-text dark:text-[var(--text-muted)] mt-0.5 truncate">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {extraAction && (
          <button
            type="button"
            onClick={extraAction.onClick}
            title={extraAction.title || extraAction.label}
            className="text-xs font-medium text-pt-gold-text dark:text-[var(--gold-text)] hover:bg-pt-gold/10 dark:hover:bg-pt-gold/15 px-3 py-1.5 rounded-lg transition-colors"
          >
            {extraAction.label}
          </button>
        )}
        <button
          onClick={onClose}
          className="p-2 hover:bg-pt-forest/5 dark:hover:bg-white/5 rounded-xl transition-colors"
          aria-label={t('close')}
        >
          <X size={20} className="text-pt-sage-text dark:text-[var(--text-muted)]" />
        </button>
      </div>
    </div>
  );
};

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className = '' }) => (
  <div className={`p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-160px)] no-scrollbar ${className}`}>
    {children}
  </div>
);

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = '' }) => (
  <div
    className={`modal-footer p-4 md:p-6 border-t border-[var(--border-subtle)] flex justify-end gap-3 ${className}`}
  >
    {children}
  </div>
);

export default Modal;
